import { Button } from "@/components/ui/button";
import { AlertTriangle, Sparkles, RefreshCw } from "lucide-react";
import { type Effort, useModelLabel } from "@/lib/llmModels";

const ActionBar = ({
  unpairedCount,
  queuedCount,
  generationActive,
  raceDisabled,
  onFillNow,
  onApplyChanges,
  onOpenModelSheet,
  onOpenRegenerateAll,
  model,
  effort,
}: {
  unpairedCount: number;
  queuedCount: number;
  generationActive: boolean;
  raceDisabled: boolean;
  onFillNow: () => void;
  onApplyChanges: () => void;
  onOpenModelSheet: () => void;
  onOpenRegenerateAll: () => void;
  model: string;
  effort: Effort;
}) => {
  const isOdd = unpairedCount % 2 !== 0;
  const actionsDisabled = generationActive || raceDisabled;
  const modelLabel = useModelLabel(model);

  return (
    <div className="sticky bottom-16 z-20 -mx-4 space-y-2 border-t bg-background/95 px-4 py-3 backdrop-blur md:bottom-0 md:pb-[calc(env(safe-area-inset-bottom)+0.75rem)]">
      {queuedCount > 0 ? (
        <Button className="w-full font-semibold" disabled={actionsDisabled} onClick={onApplyChanges}>
          <Sparkles className="mr-1.5 h-4 w-4" />
          Apply {queuedCount} change{queuedCount === 1 ? "" : "s"}
        </Button>
      ) : isOdd && unpairedCount > 0 ? (
        <div className="flex items-center gap-2 rounded-md bg-yellow-100 p-2 text-xs text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Odd number of unpaired active drivers ({unpairedCount}) — pair or bench one before filling.
        </div>
      ) : unpairedCount > 0 ? (
        <Button className="w-full font-semibold" disabled={actionsDisabled} onClick={onFillNow}>
          <Sparkles className="mr-1.5 h-4 w-4" />
          Fill in the rest ({unpairedCount} to go)
        </Button>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {queuedCount > 0 && unpairedCount > 0 && !isOdd && (
          <Button size="sm" variant="outline" disabled={actionsDisabled} onClick={onFillNow}>
            <Sparkles className="mr-1.5 h-4 w-4" /> Fill in the rest ({unpairedCount})
          </Button>
        )}
        {/* Publishing and clearing live in RoundActions, under the round
            selector — whole-round actions, not part of the generation loop. */}
        <Button size="sm" variant="outline" disabled={actionsDisabled} onClick={onOpenRegenerateAll}>
          <RefreshCw className="mr-1.5 h-4 w-4" /> Regenerate…
        </Button>
        {/* The badge is the single source of truth for which model runs — fill,
            regenerate and apply all use it. Tap to change it or the effort. */}
        <button
          type="button"
          onClick={onOpenModelSheet}
          className="ml-auto flex min-w-0 flex-col items-end rounded-md px-2.5 py-1 text-right transition-colors hover:bg-muted/50"
        >
          <span className="truncate text-xs font-medium">{modelLabel}</span>
          <span className="text-[10px] capitalize text-muted-foreground">{effort} effort</span>
        </button>
      </div>
    </div>
  );
};

export default ActionBar;
